import { CreatorResponseDto } from "./creators.dto";
import { SubmittedPostDto } from "./submitted-posts.dto";

export interface LatestPostResultsDto {
    platform: string;
    postId: string;
    views: number;
    likes: number;
    profilePicture: string;
    handle: string;
    comments: number;
    shares: number;
    save_count: number;
    engagementRate: number;
    engagements: number;
    followers: number;
    createTime: Date;
    updatedDate: Date;
    contentSummary: any;
    tiktokVideoFile: any;
    tiktokCarouselFile: any;
    instagramVideoFile: any;
    instagramCarouselFile: any;
    commentSentiment: FormattedCommentSentiment;
    submittedPost: SubmittedPostDto;
    videoLink: string;
}

export interface MonitoringLatestPostResultsDto extends LatestPostResultsDto {
    creator: CreatorResponseDto;
}

export interface FormattedCommentSentiment {
    comments: FormattedComment[];
    overall_message: string;
    positivity_rating: number;
    posivity_explaination: string;
    positivity_rating_with_likes: number;
    posivity_with_likes_explaination: string;
    createdDate: Date;
    id: string;
    deletedDate: Date;
}

export interface FormattedComment {
    text: string;
    like_count: number;
    is_high_purchase_intent: boolean;
    reply_count: number;
    user: {
        avatar_thumb: string;
        nickname: string;
        handle: string;
    };
    createdDate: number;
}

export interface ResultDto {
    id: string;
    submittedPostId: string;
    creatorId: string;
    submittedPost: SubmittedPostDto;
    creator: CreatorResponseDto;
    handle: string;
    followerCount: number;
    thumbnailUrl: string;
    socialChannel: number;
    workPlatformId: string;
    externalId: string;
    shoutBriefId: string
    dataRefreshDate: Date;
    engagement: PhylloEngagement;
    fullContentItem: any;
    photoUrl: string;
    profileUrl: string;
    profileChecks: ProfileCheck[];
}

export interface PhylloEngagement {
    like_count?: number | null;
    dislike_count?: number | null;
    comment_count?: number | null;
    impression_organic_count?: number | null;
    reach_organic_count?: number | null;
    save_count?: number | null;
    view_count?: number | null;
    replay_count?: number | null;
    watch_time_in_hours?: number | null;
    share_count?: number | null;
    impression_paid_count?: number | null;
    reach_paid_count?: number | null;
    email_open_rate?: number | null;
    email_click_rate?: number | null;
    unsubscribe_count?: number | null;
    spam_report_count?: number | null;
    click_count?: number | null;
    additional_info?: any;
    // our own play count
    play_count?: number;
    valid?: boolean;
    views?: number | null;
    clicks?: number | null;
    replies?: number | null;
    viewers?: number | null;
    screenshots?: number | null;
    interactions?: number | null;
}

export interface ProfileCheck {
    type: string;
    value: string;
    createdDate: Date;
}

export interface GenericPostsDto {
    id: string;
    title: string;
    description: string;
    image: string;
    url: string;
    platform: SocialPlatformsEnum;
    createdDate: Date;
    likes: number;
    comments: number;
    views: number;
    shares: number;
    updatedDate: Date;
    postType: string;
    postedDate: Date;
    contentSummary: ContentSummaryDto | null;
    tiktokVideoFile: TiktokVideoFileDto | null;
    tiktokCarouselFile: TiktokCarouselFileDto | null;
    instagramCarouselFile: InstagramCarouselFileDto | null;
    instagramVideoFile: InstagramVideoFileDto | null;
}

export interface ContentSummaryDto {
    id: string;
    createdDate: string; // ISO 8601 date string
    deletedDate: string | null; // null or ISO 8601 date string
    social_network: SocialNetwork;
    contentId: string;
    status: Status;
    success: boolean;
    error: string | null;
    actions: string[];
    contentType: ContentType;
    overallTone: string;
    productsShown: string[];
    brandsMentioned: string[];
    keywordsByIndustry: string[];
    processingMode: ProcessingMode;
    processingTime: string; // e.g., "13.5s"
    summarizationOnly: boolean;
    tiktok_post_id: string;
    error_message: string | null;
}

export interface TiktokCarouselFileDto {
    id: string;
    createdDate: string; // ISO 8601 date string
    deletedDate: string | null; // null or ISO 8601 date string
    processed_time: string; // e.g., "0.4564" as a string
    title: string;
    images: ImageInfo[];
}

export interface ImageInfo {
    width: number;
    height: number;
    s3_key: string;
    s3_url: string;
    s3_bucket: string;
}

type SocialNetwork = "tiktok" | "instagram" | "facebook" | "twitter" | string;
type Status = "Reviewed" | "Pending" | "Rejected" | string;
type ContentType = "lifestyle" | string;
type ProcessingMode = "summarization_only" | string;
type VideoFileStatus = "completed" | "pending" | "failed" | string;

export enum SocialPlatformsEnum {
    TIKTOK = 'TikTok',
    INSTAGRAM = 'Instagram',
    YOUTUBE = 'YouTube',
    SNAPCHAT = 'SnapChat',
    UNKNOWN = 'Unknown',
}

export interface TiktokVideoFileDto {
    id: string;
    createdDate: string; // ISO 8601 date string
    deletedDate: string | null;
    processed_time: string; // e.g., "0.4564" as a string
    title: string;
    noWatermarkMp4: string;
    watermarkMp4: string;
    region: string; // e.g., "GB"
    download_count: number;
    create_time: number; // epoch timestamp
    cover: string;
    avatar: string;
    post_id: string;
    musicInfo: MusicInfo;
    s3_url: string;
    s3_key: string;
    s3_bucket: string;
    status: VideoFileStatus;
    tiktok_post_id: string;
    /** Watch or player URL from Super Social, e.g. /video/{id} or /player/v1/{id}. */
    url?: string;
}

export interface MusicInfo {
    album: string;
    music: string;
    title: string;
    author: string;
    duration: number;
    original: boolean;
}

export interface InstagramVideoFileDto {
    cover: string;
    createdDate: string;
    deletedDate: string | null;
    duration: string;
    id: string;
    instagram_post_id: string;
    is_ad: boolean;
    is_affiliate: boolean;
    is_paid_partnership: boolean;
    is_video: boolean;
    loot_packet: any;
    musicInfo: {
        artist_name: string;
        audio_id: string;
        should_mute_audio: boolean;
        should_mute_audio_reason: string;
        song_name: string;
        uses_original_audio: boolean;
    };
    original_url: string;
    s3_bucket: string;
    s3_key: string;
    s3_url: string;
    shortcode: string;
    status: VideoFileStatus;
    title: string;
    video_id: string;
}

export interface InstagramCarouselFileDto {
    createdDate: Date;
    deletedDate: Date | null;
    id: string;
    images: ImageInfo[];
    loot_packet: any;
    instagram_post_id: string;
    is_ad: boolean;
    is_affiliate: boolean;
    is_paid_partnership: boolean;
    is_video: boolean;
    original_url: string;
    shortcode: string;
    status: VideoFileStatus;
  }